import { tmxApiCall } from './index'

// Utility
import logger from '../utility/loggerUtil'
import session from '../utility/sessionUtil'

export default {
  /**
   * Gets information to complete a new loan origination.
   * @param {String} applicationId - The identifier for the loan application.
   * @returns {Promise<{appraisalId: Number, customerId: String, maxLoanAmount: Number, minLoanAmount: Number, productId: Number, loanTypeCode: Number, locationId: Number, locationBrand: String, storeId: Number, loanValidations: Object}>}
   */
  async getOriginationInfo (applicationId) {
    logger.info('Started to get loan origination info.')

    return tmxApiCall('get', '/originationInfo', {
      headers: {
        'x-mobile-app-id-token': session.getIdToken()
      },
      params: {
        applicationId
      }
    })
  },

  /**
   * Starts process to originate a new loan for the customer.
   * @param {String} applicationId - The origination application identifier.
   * @param {Number} alternateDisburseId - The bank or card identifier that was not chosen for disbursement.
   * @param {Number} autoPayAlternateMethodId - The bank or card identifier that was not signed up for auto pay.
   * @param {Boolean} autoPayEnrolled - True if auto pay is being signed up for.
   * @param {String} autoPayMethod - The type of payment method sign up for auto pay (bank or card).
   * @param {Number} autoPayMethodId - The bank or card identifier to sign up for auto pay.
   * @param {String} autoPayMethodNumber - The last 4 digits of the bank account or card number.
   * @param {Number} disbursementAmount - The amount the user selected to originate the loan for.
   * @param {Number} [disburseBankId] - The bank account ID to disburse funds to (if disburse to bank was selected).
   * @param {Number} [disburseCardIds] - The bank card IDs to disburse funds to (if disburse to debit card was selected).
   * @returns {Promise<{messageKey: String}>} The loan origination status key.
   */
  async postLoanOriginate (applicationId, alternateDisburseId, autoPayAlternateMethodId, autoPayEnrolled, autoPayMethod, autoPayMethodId, autoPayMethodNumber, disbursementAmount, disburseBankId = undefined, disburseCardIds = undefined) {
    logger.info('Started originate loan post')
    return tmxApiCall('post', '/loanOriginate', {
      applicationId,
      autoPay: {
        alternateMethodId: autoPayAlternateMethodId,
        enrolled: autoPayEnrolled,
        method: autoPayMethod,
        methodId: autoPayMethodId,
        methodNumber: autoPayMethodNumber
      },
      disbursement: {
        amount: Number.parseFloat(disbursementAmount.toString()),
        bankId: disburseBankId,
        cardIds: disburseCardIds,
        alternateId: alternateDisburseId
      }
    })
  },

  /**
   * Updates the applicationId of the current reapply loan when the user changes from Debit Disbursement to Bank Disbursement
   * @param {String} applicationId - The origination application identifier.
   * @returns {Promise<{ApplicationId: String, LoanId: String, NewDueDate: String, LoanAmount: Number, error: String}>} The loan origination details.
   */
  async updateOrigination (applicationId) {
    logger.info('Started originate loan put')
    return tmxApiCall('put', '/loanOriginate', {
      applicationId
    })
  },

  /**
   * Gets the loan origination details, if completed.
   * @param {String} messageKey - The unique key to get the status of an origination request.
   * @returns {Promise<{loanId: String, newDueDate: String, loanAmount: Number, error: String}>} The loan origination details.
   */
  async getLoanOriginateStatus (messageKey) {
    logger.info('Started originate loan post')
    return tmxApiCall('get', '/loanOriginate', {
      params: {
        messageKey
      }
    })
  },

  /**
   * Updates an appraisals status to put a customer in a queue to be called by a representative.
   * @param {String} appraisalId - The identifier of an appraisal.
   */
  async addToPhoneQueue (appraisalId) {
    logger.info('Started put add to phone queue call.')
    return tmxApiCall('put', '/appraisalStatus', {
      appraisalId
    })
  },

  /**
   * Gets reapply approval status and a max reapply amount if approved.
   * @param {Boolean} isReapply - True if a reapply request is being made. False is a refinance is being made.
   * @param {Boolean} migrateProduct - True if a product migration refinance is being performed.
   * @param {String} productId - The productId of the payday loan the customer is eligible to reapply for.
   * @param {String} storeId - The storeId of the payday loan the customer is eligible to reapply for.
   * @param {String} [previousLoanHeaderId] - The previous loan header identifier, if doing a refi.
   * @param {String} [timezone] - The timezone on the user's device. Only saved when doing a reapply.
   * @returns {Promise<{approved: Boolean, applicationId: String, approvalAmount?: Number, achMaxDueDate?: String, achOnly?: Boolean, loanModelId?: String, originationDate?: String, storeId?: Number}>} Reapply approval status and amount.
   */
  async getUnderwritingInfo (isReapply, migrateProduct, productId, storeId, previousLoanHeaderId = undefined, timezone = undefined) {
    logger.info('Started get underwriting info call.')
    return tmxApiCall('get', '/underwriting', {
      params: {
        isReapply,
        loanHeader: previousLoanHeaderId,
        migrateProduct,
        productId,
        storeId,
        timezone
      }
    })
  }
}
